import React from 'react';
import { useConnectionRequests } from '@/hooks/useConnectionRequests';
import { Button } from '@/components/ui/button';
import AvatarContainer from '@/assets/images/avatars/avatar_container.png';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import ConnectionsIcon from '@/assets/icons/connections.svg';

interface ConnectionRequestsListProps {
  onAcceptRequest?: (requesterId: string) => Promise<void>;
  onDeclineRequest?: (requesterId: string) => Promise<void>;
}

export const ConnectionRequestsList: React.FC<ConnectionRequestsListProps> = ({ 
  onAcceptRequest, 
  onDeclineRequest 
}) => {
  const { requests, loading, error, acceptRequest, declineRequest } = useConnectionRequests();

  const handleAcceptRequest = async (requesterId: string) => {
    if (onAcceptRequest) {
      await onAcceptRequest(requesterId);
    } else {
      await acceptRequest(requesterId);
    }
  };

  const handleDeclineRequest = async (requesterId: string) => {
    if (onDeclineRequest) {
      await onDeclineRequest(requesterId);
    } else {
      await declineRequest(requesterId);
    }
  };

  if (loading) return <div className="text-center py-8">Carregando solicitações...</div>;
  if (error) return <div className="text-center text-red-500 py-8">{error}</div>;
  if (requests.length === 0) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      {/* Empty State Icon */}
      <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
        <img 
          src={ConnectionsIcon} 
          alt="Connections" 
          className="w-10 h-10 text-white/40"
          style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
        />
      </div>
      
      {/* Empty State Title */}
      <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
        No connection requests
      </h3>
      
      {/* Empty State Subtitle */}
      <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
        You haven't received any connection requests yet.
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {requests.map((profile, idx) => (
        <div key={profile.id} className="flex flex-col">
          <div className="flex items-center gap-3 min-w-0">
            <FramedAvatar src={profile.profile_picture_url} alt={profile.full_name} initials={profile.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : ''} size={42} frameSize={{ w: 50, h: 55 }} beltRank={profile.belt_rank} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <div className="font-semibold text-white truncate text-base">{profile.full_name}</div>
              </div>
              <div className="text-white/60 truncate" style={{ fontSize: '12px' }}>@{profile.username}</div>
              {profile.academy && (
                <div className="text-[10px] text-white/60 truncate">{profile.academy}</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4 w-full">
            <Button
              size="sm"
              className="rounded-full px-2 py-0 min-h-0 min-w-0 flex-1 border border-white text-white bg-transparent hover:bg-white/10 text-[13px] font-normal"
              style={{ height: 26 }}
              onClick={() => handleAcceptRequest(profile.id)}
              variant="outline"
            >
              Accept
            </Button>
            <Button
              size="sm"
              className="rounded-full px-2 py-0 min-h-0 min-w-0 flex-1 border border-white text-white bg-transparent hover:bg-white/10 text-[13px] font-normal"
              style={{ height: 26 }}
              onClick={() => handleDeclineRequest(profile.id)}
              variant="outline"
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

function getBeltColor(belt: string) {
  switch (belt.toLowerCase()) {
    case 'white': return '#fff';
    case 'blue': return '#1e40af';
    case 'purple': return '#7c3aed';
    case 'brown': return '#92400e';
    case 'black': return '#000';
    default: return '#888';
  }
}

function getBeltLabel(belt: string) {
  switch (belt.toLowerCase()) {
    case 'white': return 'White Belt';
    case 'blue': return 'Blue Belt';
    case 'purple': return 'Purple Belt';
    case 'brown': return 'Brown Belt';
    case 'black': return 'Black Belt';
    default: return belt.charAt(0).toUpperCase() + belt.slice(1);
  }
} 